import { scrollDashboardToTop } from "../../../../hooks/useScrollOnTabChange";
import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useCreateCmpEventMutation,
  useGetCmpEventDraftQuery,
  useGetCmpEventTypesQuery,
  useGetCmpEventSponsorshipTaxDefaultQuery,
  CmpEventKindEnum,
} from "../../../../graphql/types";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import EventWizard from "./wizard/EventWizard";
import { eventDraftToFormState } from "../eventDraftDocument";
import { WizardAction } from "./wizard/wizardShared";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";
import {
  buildBlankEventFormState,
  eventFormStateToInput,
  toEventTypeOptions,
} from "../eventBackendMappers";
import {
  EventFormState,
  createPixelQaEventFormState,
} from "../eventMockData";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { formatEventScheduleParts } from "../eventDateTime";
import {
  prepareEventDataForSave,
  getEventSaveErrorMessage,
} from "../eventSaveInput";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";
import { UserFacingError } from "../../../../utils/userFacingError";

const eventCreateErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
});

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";

const CreateEvent = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const draftId = new URLSearchParams(location.search).get("draftId");
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [preparingSave, setPreparingSave] = useState(false);
  const saveInProgress = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmedCreatedEvent, setConfirmedCreatedEvent] = useState<{
    id: string;
    event: EventFormState;
  } | null>(null);
  // July 9, 2026 direction: creating an event shows the centered success
  // popup (same card as Create Member's success dialog) and every close path
  // lands on Manage Events instead of the old silent jump to Update Event.
  const [successMessage, setSuccessMessage] = useState<{
    message: string;
    highlights: string[];
  } | null>(null);
  const pixelQaEnabled =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(location.search).get("pixelQa") === "1";
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "create-step-1"
  );

  const { data: eventTypesData, refetch: refetchEventTypes } =
    useGetCmpEventTypesQuery({
      variables: { input: { chamberId, includeArchived: false } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
      context: eventCreateErrorContext,
    });
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  // Claude Design (15) Step 4 note "All future events will automatically be
  // set with the same sponsor tax rules as selected." (Monday 13252617096):
  // a new event starts with the tax choices of the chamber's latest saved
  // event that offers sponsorships. A draft keeps its own saved choices.
  const { data: taxDefaultData, error: taxDefaultError } = useGetCmpEventSponsorshipTaxDefaultQuery({
    variables: { input: { chamberId } },
    skip: !chamberId || Boolean(draftId),
    fetchPolicy: "network-only",
    context: eventCreateErrorContext,
  });
  const sponsorshipTaxDefault = taxDefaultData?.getCmpEventSponsorshipTaxDefault.sponsorshipTaxMode;
  const sponsorshipTaxDefaultReady = Boolean(draftId) || Boolean(sponsorshipTaxDefault) || Boolean(taxDefaultError);
  const [createCmpEvent, { loading: creatingEvent }] =
    useCreateCmpEventMutation({ context: eventCreateErrorContext });

  // A side draft saved before Oct 2026 (CMP_EVENT_DRAFT, no event yet) still
  // opens here from Manage Events; Save Draft, Schedule or Publish turns it
  // into a real event and deletes the side record (draftId below).
  const { data: draftData } = useGetCmpEventDraftQuery({
    variables: { input: { chamberId, draftId: draftId || "" } },
    skip: !chamberId || !draftId, fetchPolicy: "network-only",
  });
  const eventTypes = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData?.getCmpEventTypes.eventTypes]
  );
  const initialEvent = useMemo(
    () => {
      if (confirmedCreatedEvent) {
        return confirmedCreatedEvent.event;
      }

      if (draftData?.getCmpEventDraft) return eventDraftToFormState(draftData.getCmpEventDraft);

      if (pixelQaEnabled) {
        const qaEventType =
          eventTypes.find((eventType) => eventType.label === "Awards Gala") ||
          eventTypes[0];
        return createPixelQaEventFormState(qaEventType);
      }

      const blank = buildBlankEventFormState(eventTypes);
      return sponsorshipTaxDefault ? { ...blank, sponsorshipTaxMode: sponsorshipTaxDefault } : blank;
    },
    [confirmedCreatedEvent, draftData, eventTypes, pixelQaEnabled, sponsorshipTaxDefault]
  );
  const timeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone || getBrowserTimeZone()
  ).timeZone;

  if (pixelQaState && !pixelQaEnabled) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  const getEventWithActiveEventType = async (
    event: EventFormState
  ): Promise<EventFormState> => {
    // Community/Member events use a fixed type that is not in the chamber's
    // event type list; the backend assigns it, so skip the lookup.
    if (event.eventKind === CmpEventKindEnum.Community) {
      return { ...event, eventType: "Community/Member Event" };
    }
    const latest = await refetchEventTypes();
    const latestEventTypes = toEventTypeOptions(
      latest.data?.getCmpEventTypes.eventTypes || []
    );
    const selected = latestEventTypes.find(
      (eventType) => eventType.id === event.eventTypeId
    );
    if (selected) {
      return { ...event, eventType: selected.label, eventTypeColor: selected.color };
    }
    throw new UserFacingError(
      "The Event Type you chose is no longer in the list. In Step 1 Event Details, select another Event Type, then save again."
    );
  };

  const handleSubmit = async ({
    event,
    action,
    leaveAfterSave = false,
    step,
  }: {
    event: EventFormState;
    action: WizardAction;
    leaveAfterSave?: boolean;
    step?: number;
  }) => {
    if (saveInProgress.current) return false;
    saveInProgress.current = true;
    setPreparingSave(true);
    setErrorMessage(null);
    try {
      if (!chamberId) {
        throw new UserFacingError(`Your ${noun.singular} details are still loading. Wait a moment, then try again.`);
      }
      // Claude Design CES onSaveDraft persistEvent('Draft') (Monday
      // 13290832990): Save Draft creates a real Draft event, not a side copy.
      const statusEvent: EventFormState = {
        ...event,
        status: action === "draft" ? "Draft" : action === "schedule" ? "Scheduled" : "Published",
      };
      const eventWithActiveEventType =
        await getEventWithActiveEventType(statusEvent);
      const result = await createCmpEvent({
        variables: {
          input: {
            chamberId,
            draftId: event.draftId,
            expectedDraftRevision: event.draftRevision,
            data: await prepareEventDataForSave(
              eventFormStateToInput({
                event: eventWithActiveEventType,
                timeZone,
              })
            ),
          },
        },
      });
      const savedEvent = result.data?.createCmpEvent.event;
      if (!savedEvent?.id) {
        throw new Error("The event could not be saved.");
      }
      // Keep the route guard mounted until it resumes the selected route.
      if (leaveAfterSave) return true;
      if (action === "draft") {
        // The saved Draft continues on Update Event at the same step, which
        // shows "Event saved as draft." (Create Event Screen onSaveDraft).
        navigate(RoutePaths.chamberAdmin_events_update.replace(":eventId", savedEvent.id), {
          replace: true,
          state: { eventSavedMessage: "Event saved as draft.", step },
        });
        return true;
      }
      // The mutation confirmed this exact form snapshot. Remount the wizard
      // with it as the saved baseline before any route change. This keeps the
      // guard active during the request, but clears it after persistence.
      setConfirmedCreatedEvent({
        id: savedEvent.id,
        event: eventWithActiveEventType,
      });
      const eventName = event.title.trim() || "Untitled Event";
      if (action === "publish") {
        setSuccessMessage({
          message: `The event ${eventName} has been published successfully and is now live on your website.`,
          highlights: [eventName],
        });
      } else {
        const { date, time } = formatEventScheduleParts(
          event.publishAtUtcMilli || Date.now(),
          timeZone
        );
        setSuccessMessage({
          message: `The event ${eventName} has been scheduled to publish on ${date} at ${time}.`,
          highlights: [eventName, date, time],
        });
      }
      return true;
    } catch (error) {
      setErrorMessage(getEventSaveErrorMessage(error));
      scrollDashboardToTop();
      return false;
    } finally {
      saveInProgress.current = false;
      setPreparingSave(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessMessage(null);
    navigate(RoutePaths.chamberAdmin_events_manage);
  };

  return (
    <>
      {/* Claude Design ActionSuccessModal. Every close path goes through
          handleCloseSuccessDialog, which redirects to Manage Events after
          Publish or Schedule (July 9, 2026 direction). Save Draft continues
          on Update Event. */}
      <ActionSuccessDialog
        message={successMessage?.message}
        highlights={successMessage?.highlights}
        onClose={handleCloseSuccessDialog}
      />
      <ActionSuccessDialog
        tone="error"
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
      {/* The wizard waits for the tax default so it never replaces a form
          the user has started (an error falls back to taxed sponsorships). */}
      {sponsorshipTaxDefaultReady || pixelQaEnabled ? <EventWizard
        // Publish/Schedule remount on the confirmed event before the page
        // leaves for Manage Events.
        key={confirmedCreatedEvent ? confirmedCreatedEvent.id : "new-event"}
        mode="create"
        initialEvent={initialEvent}
        eventTypes={eventTypes}
        chamberId={chamberId}
        timeZone={timeZone}
        saving={preparingSave || creatingEvent}
        allowStepJumpWithoutValidation={pixelQaEnabled}
        onSubmit={handleSubmit}
      /> : null}
    </>
  );
};

export default CreateEvent;
